
import { FileCode, Laptop, SquareCode, Zap, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export const FeatureCard = ({
  icon,
  title,
  description
}: FeatureCardProps) => {
  return (
    <Card className="card-hover">
      <CardContent className="p-6 flex flex-col items-center text-center">
        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          {icon}
        </div>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
};

const FeaturesSection = () => {
  return (
    <section id="features" className="section-padding scroll-section">
      <div className="container flex flex-col items-center">
        <div className="w-full flex justify-center mb-6">
          <div 
            className="justify-center font-medium flex items-center gap-1 px-3 py-2 rounded-full bg-[#44d3e91a] border border-[#53CADE] text-[#343636] text-xs sm:text-sm text-center max-w-[90vw] sm:max-w-none leading-snug"
          >
            <Sparkles className="size-4 text-[#53CADE]" />
            AI-enhanced tasks and full context for your AI coding agent
          </div>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
          Supercharge Your AI Coding Experience
        </h2>
        
        <p className="text-center text-muted-foreground mb-12 max-w-3xl mx-auto text-base">
          <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">AI code assistants</span> lose track of your project between sessions. CodeRide maintains <strong className="font-semibold text-gray-900">persistent</strong> <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">context</span> so your AI remembers your architectural decisions, coding patterns, and project goals.{" "}
          <button 
            onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
            className="inline-flex items-center text-base font-bold text-coderide-light-blue hover:text-coderide-dark-blue transition-colors"
          >
            <span className="underline">Start building</span> →
          </button>
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard 
            icon={<FileCode />} 
            title="No More Re-explaining" 
            description="Stop repeating project context. AI remembers your codebase structure and dependencies between sessions." 
          />
          <FeatureCard 
            icon={<Zap />} 
            title="AI-Enhanced Tasks" 
            description="Each task comes pre-loaded with optimized context and instructions, making your AI agent more effective from the start." 
          />
          <FeatureCard 
            icon={<SquareCode />} 
            title="Iterative Development" 
            description="Build on previous work seamlessly. AI understands your project evolution and coding style." 
          />
          <FeatureCard 
            icon={<Laptop />} 
            title="Consistent Results" 
            description="Get reliable AI suggestions that align with your existing code patterns and project conventions." 
          />
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
